import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { getConfig } from '../../utils/config';

const API_URL_BASE = getConfig('API_URL') || 'https://api.jpro.app/api/v1';
const cleanUrl = API_URL_BASE.endsWith('/') ? API_URL_BASE.slice(0, -1) : API_URL_BASE;
const API_URL = cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`;

function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [website, setWebsite] = useState(''); // honeypot — bots fill every field, humans don't
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            setError('Please enter your email address');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/auth/forgot_password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email.trim().toLowerCase(),
                    website
                })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                setEmail('');
            } else {
                setError(data.error || 'Failed to send reset instructions');
            }
        } catch (err) {
            console.error('Forgot password error:', err);
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
                        <h2 className="text-3xl font-unna font-bold text-gray-900 mb-2">
                            Check Your Email
                        </h2>
                        <p className="text-gray-600 font-rasa mb-6">
                            If an account with that email exists, we've sent password reset instructions to your inbox.
                        </p>
                        <p className="text-sm text-gray-500 font-rasa mb-8">
                            Didn't receive an email? Check your spam folder or try again with a different email address.
                        </p>
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-rasa font-medium"
                        >
                            <ArrowLeft size={16} />
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-rasa font-medium mb-6"
                    >
                        <ArrowLeft size={16} />
                        Back to Login
                    </Link>
                    
                    <div className="text-center">
                        <Mail className="mx-auto h-12 w-12 text-blue-600 mb-4" />
                        <h2 className="text-3xl font-unna font-bold text-gray-900">
                            Forgot Password?
                        </h2>
                        <p className="mt-2 text-gray-600 font-rasa">
                            Enter your email address and we'll send you instructions to reset your password.
                        </p>
                    </div>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {/* Honeypot: offscreen, not focusable, aria-hidden. Real users never fill this;
                        bots that auto-fill every input will trip it. Don't use display:none — some bots detect that. */}
                    <div
                        aria-hidden="true"
                        style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                    >
                        <label htmlFor="website">Website (leave blank)</label>
                        <input
                            type="text"
                            id="website"
                            name="website"
                            tabIndex="-1"
                            autoComplete="off"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                        />
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                            <AlertCircle size={16} />
                            <span className="font-rasa">{error}</span>
                        </div>
                    )}

                    <div>
                        <label htmlFor="email" className="block text-sm font-rasa font-medium text-gray-700 mb-2">
                            Email Address
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full text-gray-800 dark:text-gray-200 px-3 py-2 border border-gray-300 rounded-lg font-rasa placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter your email address"
                            disabled={loading}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !email.trim()}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-rasa font-medium"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Sending Instructions...
                            </>
                        ) : (
                            'Send Reset Instructions'
                        )}
                    </button>
                </form>

                <div className="text-center">
                    <p className="text-sm text-gray-600 font-rasa">
                        Remember your password?{' '}
                        <Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium">
                            Sign in here
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default ForgotPassword;